import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import * as d3 from 'd3';
import { HourlyForecastTimelineSegment } from '../../models/HourlyForecastTimelineData';
import { VerificationData } from '../../hooks/timeline/useVerificationData';
import { formatVisibility } from '../../utils/visibilityFormatters';
import {
  getSegmentColor, getVisibilityLabel, getVisibilityColor,
  NO_DATA_COLOR, NOW_LINE_COLOR,
} from '../../utils/timelineVisibility';
import VerificationDebugHover from './VerificationDebugHover';

interface VerificationTimelineProps {
  data: VerificationData;
  useUTCTime: boolean;
}

interface HoverInfo {
  time: Date;
  x: number;
  y: number;
  segment: HourlyForecastTimelineSegment | null;
  rowLabel: string;
  isActuals: boolean;
}

interface DebugHoverContext {
  segment: HourlyForecastTimelineSegment | null;
  rowLabel: string;
  isActuals: boolean;
  requestedTime: Date | null;
}

const VerificationTimeline: React.FC<VerificationTimelineProps> = ({
  data,
  useUTCTime
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [debugHover, setDebugHover] = useState<DebugHoverContext | null>(null);
  const [debugHoverVisible, setDebugHoverVisible] = useState(true);

  const isDebugMode = useMemo(() =>
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debug') === 'true',
  []);

  const getHours = (date: Date): number => useUTCTime ? date.getUTCHours() : date.getHours();
  const getMinutes = (date: Date): number => useUTCTime ? date.getUTCMinutes() : date.getMinutes();

  const formatTime = (date: Date): string => {
    const hours = getHours(date);
    const minutes = getMinutes(date);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatTimeRange = (startTime: Date, intervalMinutes: number = 60): string => {
    const endTime = new Date(startTime.getTime() + (intervalMinutes - 1) * 60 * 1000 + 59 * 1000);
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const actualsInterval = data.actuals?.segment_interval || 60;

  useEffect(() => {
    if (!svgRef.current || !data.actuals) return;

    d3.select(svgRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 60, bottom: 35, left: 130 };
    const rowHeight = 40;
    const forecastRowGap = 20;
    const actualsGap = 36; // Larger gap after actuals row
    const lineExtension = Math.floor(rowHeight / 3);
    const nowLineWidth = 3;
    const borderColor = 'rgba(255, 255, 255, 0.3)';
    // Two 30-min actuals cells align with one 60-min prediction cell
    const actualsCellWidth = 128;
    const predCellWidth = actualsCellWidth * (60 / actualsInterval);

    const rowLabels = ['Actuals', 'Forecast 1h prior', 'Forecast 2h prior', 'Forecast 3h prior', 'Forecast 4h prior'];
    const totalContentHeight = rowHeight + actualsGap + 4 * rowHeight + 3 * forecastRowGap;

    const now = data.referenceTime;
    const centerPoint = new Date(now);
    const minutes = getMinutes(centerPoint);
    const targetMinutes = Math.floor(minutes / 15) * 15;
    if (useUTCTime) {
      centerPoint.setUTCMinutes(targetMinutes, 0, 0);
    } else {
      centerPoint.setMinutes(targetMinutes, 0, 0);
    }

    const startTime = new Date(centerPoint);
    if (useUTCTime) {
      startTime.setUTCHours(getHours(centerPoint) - 4);
    } else {
      startTime.setHours(getHours(centerPoint) - 4);
    }

    const numActualsCells = (4 * 60) / actualsInterval;
    const numPredCells = 4;
    const contentWidth = numActualsCells * actualsCellWidth;
    const width = contentWidth + margin.left + margin.right;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', totalContentHeight + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${width} ${totalContentHeight + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const timeScale = d3.scaleTime()
      .domain([startTime, centerPoint])
      .range([0, contentWidth]);

    const actualsIncrements: Date[] = [];
    for (let i = 0; i < numActualsCells; i++) {
      actualsIncrements.push(new Date(startTime.getTime() + i * actualsInterval * 60 * 1000));
    }

    const predIncrements: Date[] = [];
    for (let i = 0; i < numPredCells; i++) {
      predIncrements.push(new Date(startTime.getTime() + i * 60 * 60 * 1000));
    }

    const hourMarkers: Date[] = [];
    for (let i = 0; i <= 4; i++) {
      hourMarkers.push(new Date(startTime.getTime() + i * 60 * 60 * 1000));
    }

    const findSegment = (segments: HourlyForecastTimelineSegment[], time: Date, interval: number): HourlyForecastTimelineSegment | null => {
      if (interval === 30) {
        const halfInterval = 15 * 60 * 1000;
        return segments.find(s => Math.abs(s.time.getTime() - time.getTime()) <= halfInterval) || null;
      }
      const targetHour = useUTCTime ? time.getUTCHours() : time.getHours();
      return segments.find(s => {
        const segHour = useUTCTime ? s.time.getUTCHours() : s.time.getHours();
        return segHour === targetHour;
      }) || null;
    };

    // Calculate Y position for each row
    const getRowY = (rowIndex: number): number => {
      if (rowIndex === 0) return 0;
      return rowHeight + actualsGap + (rowIndex - 1) * (rowHeight + forecastRowGap);
    };

    rowLabels.forEach((label, rowIndex) => {
      const rowY = getRowY(rowIndex);
      const isActualsRow = rowIndex === 0;
      const segments = isActualsRow
        ? data.actuals!.segments
        : data.predictions[rowIndex - 1]?.segments || [];
      const increments = isActualsRow ? actualsIncrements : predIncrements;
      const thisCellWidth = isActualsRow ? actualsCellWidth : predCellWidth;
      const cellInterval = isActualsRow ? actualsInterval : 60;

      // Row label: two-line for forecast rows to emphasise lead time
      if (isActualsRow) {
        svg.append('text')
          .attr('x', -12)
          .attr('y', rowY + rowHeight / 2)
          .attr('text-anchor', 'end')
          .attr('fill', '#ffffff')
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .attr('dominant-baseline', 'central')
          .text('Actuals');
      } else {
        const leadNum = rowIndex;
        // "Forecast" line
        svg.append('text')
          .attr('x', -12)
          .attr('y', rowY + rowHeight / 2 - 8)
          .attr('text-anchor', 'end')
          .attr('fill', '#999')
          .attr('font-size', '11px')
          .attr('dominant-baseline', 'central')
          .text('Forecast');
        // Bold lead time line
        svg.append('text')
          .attr('x', -12)
          .attr('y', rowY + rowHeight / 2 + 8)
          .attr('text-anchor', 'end')
          .attr('fill', '#ffffff')
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .attr('dominant-baseline', 'central')
          .text(`${leadNum}h prior`);
      }

      // Bright border around entire actuals row
      if (isActualsRow) {
        svg.append('rect')
          .attr('x', -1)
          .attr('y', rowY - 1)
          .attr('width', contentWidth + 2)
          .attr('height', rowHeight + 2)
          .attr('fill', 'none')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2)
          .attr('rx', 2);
      }

      increments.forEach((time, cellIndex) => {
        const segment = findSegment(segments, time, cellInterval);
        const x = cellIndex * thisCellWidth;
        const color = segment ? getSegmentColor(segment) : NO_DATA_COLOR;

        svg.append('rect')
          .attr('class', `time-cell row-${rowIndex}`)
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', thisCellWidth)
          .attr('height', rowHeight)
          .attr('fill', color);

        svg.append('rect')
          .attr('class', `cell-border r${rowIndex}c${cellIndex}`)
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', thisCellWidth)
          .attr('height', rowHeight)
          .attr('fill', 'none')
          .attr('stroke', borderColor)
          .attr('stroke-width', 1);

        svg.append('rect')
          .attr('class', 'hover-area')
          .attr('x', x)
          .attr('y', rowY)
          .attr('width', thisCellWidth)
          .attr('height', rowHeight)
          .attr('fill', 'transparent')
          .attr('cursor', 'pointer')
          .on('mouseenter', function(event) {
            svg.selectAll(`.cell-border.r${rowIndex}c${cellIndex}`)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3);

            if (isDebugMode) {
              setDebugHover({
                segment: segment || null,
                rowLabel: label,
                isActuals: isActualsRow,
                requestedTime: isActualsRow ? null : data.predictions[rowIndex - 1]?.requestedTime || null
              });
              setDebugHoverVisible(true);
            }

            const containerBounds = containerRef.current?.getBoundingClientRect();
            if (containerBounds) {
              setHoverInfo({
                time,
                x: event.clientX - containerBounds.left,
                y: event.clientY - containerBounds.top,
                segment: segment || null,
                rowLabel: label,
                isActuals: isActualsRow
              });
            }
          })
          .on('mouseleave', function() {
            svg.selectAll(`.cell-border.r${rowIndex}c${cellIndex}`)
              .attr('stroke', borderColor)
              .attr('stroke-width', 1);
            setHoverInfo(null);
          });
      });
    });

    // Horizontal divider line between actuals and forecast rows
    const dividerY = rowHeight + actualsGap / 2;
    svg.append('line')
      .attr('x1', 0)
      .attr('y1', dividerY)
      .attr('x2', contentWidth)
      .attr('y2', dividerY)
      .attr('stroke', 'rgba(255, 255, 255, 0.15)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '6,4');

    hourMarkers.forEach((marker, i) => {
      const x = timeScale(marker);
      const isCenter = i === 4;
      svg.append('line')
        .attr('x1', x)
        .attr('y1', -lineExtension)
        .attr('x2', x)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', isCenter ? 2.5 : 1.5)
        .attr('stroke-opacity', isCenter ? 1 : 0.8);
    });

    hourMarkers.forEach(marker => {
      const x = timeScale(marker);
      svg.append('text')
        .attr('x', x)
        .attr('y', totalContentHeight + lineExtension + 18)
        .attr('text-anchor', 'middle')
        .attr('fill', '#ffffff')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('opacity', 1)
        .text(formatTime(marker));
    });

    const nowPosition = timeScale(now);
    if (nowPosition >= 0 && nowPosition <= contentWidth) {
      const nowTimeString = `Now - ${formatTime(now)}`;

      const tempText = svg.append('text')
        .attr('x', 0).attr('y', 0)
        .attr('font-size', '13px').attr('font-weight', 'bold')
        .style('visibility', 'hidden')
        .text(nowTimeString);
      const textBBox = tempText.node()?.getBBox() || { width: 100, height: 20 };
      tempText.remove();

      const labelPadding = { x: 6, y: 4 };
      const labelHeight = textBBox.height + labelPadding.y * 2;
      const labelWidth = textBBox.width + labelPadding.x * 2;
      const labelY = -lineExtension - 30;

      svg.append('rect')
        .attr('x', nowPosition - (labelWidth / 2))
        .attr('y', labelY)
        .attr('width', labelWidth)
        .attr('height', labelHeight)
        .attr('rx', labelHeight / 2)
        .attr('ry', labelHeight / 2)
        .attr('fill', NOW_LINE_COLOR);

      svg.append('text')
        .attr('x', nowPosition)
        .attr('y', labelY + (labelHeight / 2))
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('dominant-baseline', 'central')
        .text(nowTimeString);

      svg.append('line')
        .attr('x1', nowPosition)
        .attr('y1', labelY + labelHeight)
        .attr('x2', nowPosition)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', NOW_LINE_COLOR)
        .attr('stroke-width', nowLineWidth + 3)
        .attr('stroke-opacity', 0.3)
        .attr('stroke-linecap', 'round');

      svg.append('line')
        .attr('x1', nowPosition)
        .attr('y1', labelY + labelHeight)
        .attr('x2', nowPosition)
        .attr('y2', totalContentHeight + lineExtension)
        .attr('stroke', NOW_LINE_COLOR)
        .attr('stroke-width', nowLineWidth)
        .attr('stroke-linecap', 'round');
    }

  }, [data, useUTCTime]);

  if (!data.actuals) return null;

  // Actuals row + gap + 4 forecast rows with gaps + margins
  const totalSvgHeight = 40 + 36 + 4 * 40 + 3 * 20 + 40 + 35;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        overflowX: 'auto',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 1,
      }}
    >
      <svg
        ref={svgRef}
        style={{
          width: '100%',
          height: `${totalSvgHeight}px`,
          minWidth: 0
        }}
      />

      {hoverInfo && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            left: `${Math.min(hoverInfo.x + 15, containerRef.current?.clientWidth ? containerRef.current.clientWidth - 200 : hoverInfo.x)}px`,
            top: `${Math.max(hoverInfo.y - 100, 0)}px`,
            padding: 1.5,
            minWidth: 200,
            zIndex: 1000,
            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
            {hoverInfo.rowLabel}: {formatTimeRange(hoverInfo.time, hoverInfo.isActuals ? actualsInterval : 60)}
          </Typography>
          {hoverInfo.segment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              {hoverInfo.isActuals ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_hmean),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)'
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Harmonic mean: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_hmean)} (displayed)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.raw_actuals?.visibility_median),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)'
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Median: {formatVisibility(hoverInfo.segment.raw_actuals?.visibility_median)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: 16, height: 16,
                      backgroundColor: getVisibilityColor(hoverInfo.segment.lowest_visibility_level),
                      mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)'
                    }} />
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      Range: {formatVisibility(hoverInfo.segment.lowest_visibility_level)} - {formatVisibility(hoverInfo.segment.highest_visibility_level)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 16, height: 16,
                    backgroundColor: getSegmentColor(hoverInfo.segment),
                    mr: 1, borderRadius: 0.5, border: '1px solid rgba(255, 255, 255, 0.3)'
                  }} />
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    Visibility level: {getVisibilityLabel(hoverInfo.segment)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {!hoverInfo.segment && (
            <Typography variant="body2" sx={{ fontSize: '14px', color: 'text.secondary' }}>
              No data
            </Typography>
          )}
        </Paper>
      )}

      {isDebugMode && debugHover && (
        <VerificationDebugHover
          lastHoveredSegment={debugHover.segment}
          rowLabel={debugHover.rowLabel}
          isActuals={debugHover.isActuals}
          requestedTime={debugHover.requestedTime}
          segmentInterval={debugHover.isActuals ? actualsInterval : 60}
          formatTimeRange={(time: Date, interval: number) => formatTimeRange(time, interval)}
          visible={debugHoverVisible}
          onDismiss={() => setDebugHoverVisible(false)}
        />
      )}
    </Box>
  );
};

export default VerificationTimeline;
